import { datadogRum } from "@datadog/browser-rum";
import type { NextRouter } from "next/router";

interface ActiveNavigation {
  from: string;
  startedAt: number;
}

const NAVIGATION_ACTION = "frontend_navigation";

function navigationContext(
  url: string,
  activeNavigation: ActiveNavigation,
  outcome: "completed" | "failed",
  error?: Error,
) {
  return {
    from: activeNavigation.from,
    to: url,
    duration_ms: Math.round(performance.now() - activeNavigation.startedAt),
    outcome,
    ...(error ? { error: error.message } : {}),
  };
}

/**
 * Records client-side Pages Router transitions. The action duration covers the
 * Next route payload/SSR wait and React route mount, while RUM resource events
 * and query telemetry explain what happened after the page mounted.
 */
export function installDatadogNavigationInstrumentation(
  router: Pick<NextRouter, "asPath" | "events">,
) {
  if (
    typeof window === "undefined" ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  ) {
    return () => undefined;
  }

  let activeNavigation: ActiveNavigation | null = null;

  const onRouteChangeStart = (url: string) => {
    activeNavigation = {
      from: router.asPath,
      startedAt: performance.now(),
    };
  };

  const onRouteChangeComplete = (url: string) => {
    if (activeNavigation == null) {
      return;
    }

    datadogRum.addAction(NAVIGATION_ACTION, navigationContext(url, activeNavigation, "completed"));
    activeNavigation = null;
  };

  const onRouteChangeError = (error: Error, url: string) => {
    if (activeNavigation == null) {
      return;
    }

    datadogRum.addAction(
      NAVIGATION_ACTION,
      navigationContext(url, activeNavigation, "failed", error),
    );
    activeNavigation = null;
  };

  router.events.on("routeChangeStart", onRouteChangeStart);
  router.events.on("routeChangeComplete", onRouteChangeComplete);
  router.events.on("routeChangeError", onRouteChangeError);

  return () => {
    router.events.off("routeChangeStart", onRouteChangeStart);
    router.events.off("routeChangeComplete", onRouteChangeComplete);
    router.events.off("routeChangeError", onRouteChangeError);
  };
}
